package com.schoolconnect.app.feature.schools.details

import com.schoolconnect.app.datasource.network.response.SchoolAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.SchoolResponse
import com.schoolconnect.app.feature.schools.SchoolRowUi
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

data class SchoolDetailsUiState(
    val schoolName: String = "Aurora Public School",
    val schoolCode: String = "AUR00001",
    val board: String = "CBSE",
    val schoolType: String = "PRIVATE_UNAIDED",
    val medium: String = "ENGLISH",
    val isActive: Boolean = true,
    val schoolPhone: String = "9876543210",
    val schoolEmail: String = "info@aurora.edu.in",
    val primaryContactName: String = "Meera Iyer",
    val primaryContactPhone: String = "9876543211",
    val primaryContactEmail: String = "meera.iyer@aurora.edu.in",
    val secondaryContactName: String = "Rahul Iyer",
    val secondaryContactPhone: String = "9876543212",
    val secondaryContactEmail: String = "rahul.iyer@aurora.edu.in",
    val addressLine1: String = "12 Main Road, Sector 5",
    val addressLine2: String = "Near Bus Stand",
    val city: String = "Delhi",
    val district: String = "South Delhi",
    val state: String = "Delhi",
    val country: String = "India",
    val pinCode: String = "110021",
    val createdOn: String = "10 Jun 2024",
    val createdBy: String = "School Administrator",
    val lastUpdated: String = "08 Jun 2026",
    val updatedBy: String = "Super Admin",
    val schoolLogoUrl: String = "",
    val schoolLogoBytes: ByteArray? = null,
    val isSchoolLogoLoading: Boolean = false,
    val isCreatedByLoading: Boolean = false,
    val isUpdatedByLoading: Boolean = false,
    val analytics: SchoolAnalyticsResponse? = null,
    val isAnalyticsLoading: Boolean = false,
    val isLoading: Boolean = false,
    val errorMessage: String = ""
) {
    val metaLine: String = buildList {
        if (schoolCode.isNotBlank()) add(schoolCode)
        if (board.isNotBlank()) add(SystemLookupOptions.label("SCHOOL_BOARD", board))
        if (schoolType.isNotBlank()) add(SystemLookupOptions.label("SCHOOL_TYPE", schoolType))
        if (medium.isNotBlank()) add(SystemLookupOptions.label("SCHOOL_MEDIUM", medium))
    }.joinToString(" • ")

    val statusLabel: String = if (isActive) "Active" else "Inactive"

    companion object {
        fun fromResponse(school: SchoolResponse): SchoolDetailsUiState = SchoolDetailsUiState(
            schoolName = school.name.orEmpty(),
            schoolCode = school.code.orEmpty(),
            board = school.board.orEmpty(),
            schoolType = school.schoolType.orEmpty(),
            medium = school.medium.orEmpty(),
            isActive = school.isActive == true,
            schoolPhone = school.schoolPhone.orEmpty(),
            schoolEmail = school.schoolEmail.orEmpty(),
            primaryContactName = school.primaryContactName.orEmpty(),
            primaryContactPhone = school.primaryContactPhone.orEmpty(),
            primaryContactEmail = school.primaryContactEmail.orEmpty(),
            secondaryContactName = school.secondaryContactName.orEmpty(),
            secondaryContactPhone = school.secondaryContactPhone.orEmpty(),
            secondaryContactEmail = school.secondaryContactEmail.orEmpty(),
            addressLine1 = school.addressLine1.orEmpty(),
            addressLine2 = school.addressLine2.orEmpty(),
            city = school.city.orEmpty(),
            district = school.district.orEmpty(),
            state = school.state.orEmpty(),
            country = school.country.orEmpty(),
            pinCode = school.pinCode.orEmpty(),
            createdOn = formatDateText(school.createdAt.orEmpty()),
            createdBy = school.createdByName.orEmpty(),
            lastUpdated = formatDateText(school.updatedAt.orEmpty()),
            updatedBy = school.updatedByName.orEmpty(),
            schoolLogoUrl = school.schoolLogoUrl.orEmpty(),
            isLoading = false,
            errorMessage = ""
        )

        fun fromSchool(school: SchoolRowUi?): SchoolDetailsUiState {
            val row = school ?: defaultSchoolRow()
            return SchoolDetailsUiState(
                schoolName = row.name,
                schoolCode = row.code,
                board = row.board,
                schoolType = row.schoolType,
                medium = row.medium,
                isActive = row.isActive,
                schoolPhone = row.schoolPhone,
                schoolEmail = row.schoolEmail,
                primaryContactName = row.contactName,
                primaryContactPhone = row.primaryPhone,
                primaryContactEmail = row.primaryEmail,
                secondaryContactName = row.secondaryContactName,
                secondaryContactPhone = row.secondaryPhone,
                secondaryContactEmail = row.secondaryEmail,
                addressLine1 = row.addressLine1,
                addressLine2 = row.addressLine2,
                city = row.city,
                district = row.district,
                state = row.state,
                country = row.country,
                pinCode = row.pinCode,
                createdOn = formatDateText(row.createdOn),
                createdBy = row.createdBy,
                lastUpdated = formatDateText(row.updatedOn),
                updatedBy = row.updatedBy,
                schoolLogoUrl = row.schoolLogoUrl,
                isLoading = false,
                errorMessage = ""
            )
        }

        private fun defaultSchoolRow(): SchoolRowUi = SchoolRowUi(
            id = "aurora-public-school",
            name = "Aurora Public School",
            code = "AUR00001",
            city = "Delhi",
            contactName = "Meera Iyer",
            students = 801,
            isActive = true
        )

        private fun formatDateText(value: String): String {
            val normalized = value.trim()
            if (normalized.isBlank()) return "—"
            parseSchoolDetailDateTime(normalized)?.let { local ->
                val day = local.dayOfMonth.toString().padStart(2, '0')
                val time = formatSchoolDetailTime(local.hour, local.minute)
                return "$day ${shortMonthLabel(local.monthNumber)} ${local.year}, $time"
            }
            val datePart = normalized.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return normalized
            val parts = datePart.split("-")
            if (parts.size != 3) return normalized
            val year: String
            val month: Int
            val day: Int
            when {
                parts[0].length == 4 -> {
                    year = parts[0]
                    month = parts[1].toIntOrNull() ?: return normalized
                    day = parts[2].toIntOrNull() ?: return normalized
                }
                parts[2].length == 4 -> {
                    year = parts[2]
                    month = parts[1].toIntOrNull() ?: return normalized
                    day = parts[0].toIntOrNull() ?: return normalized
                }
                else -> return normalized
            }
            if (month !in 1..12 || day !in 1..31) return normalized
            return "${day.toString().padStart(2, '0')} ${shortMonthLabel(month)} $year"
        }

        private fun parseSchoolDetailDateTime(value: String): LocalDateTime? {
            if (value.length < 16 || !value.contains('T')) return null
            return runCatching {
                Instant.parse(value).toLocalDateTime(TimeZone.currentSystemDefault())
            }.getOrElse {
                val withoutZone = value.substringBefore('+').substringBefore('Z')
                runCatching {
                    LocalDateTime.parse(withoutZone)
                        .toInstant(TimeZone.UTC)
                        .toLocalDateTime(TimeZone.currentSystemDefault())
                }.getOrNull()
            }
        }

        private fun formatSchoolDetailTime(hour: Int, minute: Int): String {
            val suffix = if (hour < 12) "AM" else "PM"
            val displayHour = when (val normalized = hour % 12) {
                0 -> 12
                else -> normalized
            }
            return "${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} $suffix"
        }

        private fun shortMonthLabel(month: Int): String = when (month) {
            1 -> "Jan"
            2 -> "Feb"
            3 -> "Mar"
            4 -> "Apr"
            5 -> "May"
            6 -> "Jun"
            7 -> "Jul"
            8 -> "Aug"
            9 -> "Sep"
            10 -> "Oct"
            11 -> "Nov"
            12 -> "Dec"
            else -> ""
        }
    }
}

internal fun schoolDetailTitleCase(value: String): String =
    value.trim()
        .split(Regex("\\s+"))
        .filter { it.isNotEmpty() }
        .joinToString(" ") { word ->
            word.lowercase().replaceFirstChar { it.uppercase() }
        }

sealed interface SchoolDetailsUiEvent {
    data object BackClicked : SchoolDetailsUiEvent
    data object CloseClicked : SchoolDetailsUiEvent
}
